/**
 * DPL DEW — Authentication Types
 * 
 * Complete type definitions for role-based authentication system
 * with proper separation of admin, tenant, and unassigned user domains
 */

// Core role types
export type UserRole = 'admin' | 'tenant' | 'unassigned' | 'error';

// Admin user roles
export type AdminRole = 'master_admin' | 'operator' | 'support' | 'viewer';

// Tenant user roles  
export type TenantRole = 'owner' | 'admin' | 'member' | 'viewer';

// Permission types
export type Permission = 
  // Admin permissions
  | 'tenant_management'
  | 'billing_oversight'
  | 'analytics'
  | 'monitoring'
  | 'support_access'
  | 'platform_configuration'
  // Tenant permissions
  | 'quotations'
  | 'invoices'
  | 'accounting'
  | 'catalog'
  | 'team_management'
  | 'workspace_settings';

// Base authentication context
export interface BaseAuthContext {
  role: UserRole;
  user_id: string | null;
  tenant_id: string | null;
  is_active: boolean;
  last_login_at: string;
  error?: string;
}

// Admin authentication context
export interface AdminAuthContext extends BaseAuthContext {
  role: 'admin';
  admin_id: string;
  admin_email: string;
  full_name: string;
  admin_role: AdminRole;
  permissions: Record<string, any>;
  capabilities: Permission[];
  tenant_id: null; // Explicitly null for admins
}

// Tenant authentication context
export interface TenantAuthContext extends BaseAuthContext {
  role: 'tenant';
  tenant_name: string;
  tenant_slug: string;
  tenant_plan: string;
  tenant_status: string;
  user_role: TenantRole;
  user_permissions: Permission[];
  profile: UserProfile | null;
}

// Unassigned user context
export interface UnassignedAuthContext extends BaseAuthContext {
  role: 'unassigned';
  needs_onboarding: true;
  tenant_id: null;
}

// Error context
export interface ErrorAuthContext extends BaseAuthContext {
  role: 'error';
  error: string;
  detected_role?: string;
}

// Union type for all auth contexts
export type AuthContext = 
  | AdminAuthContext
  | TenantAuthContext
  | UnassignedAuthContext
  | ErrorAuthContext;

// User profile type
export interface UserProfile {
  full_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  bio?: string;
}

// Authentication state
export interface AuthState {
  context: AuthContext | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

// Bootstrap result
export interface BootstrapResult {
  success: boolean;
  context: AuthContext | null;
  error?: string;
  redirectPath?: string;
}

// Route guard props
export interface RouteGuardProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  fallbackPath?: string;
}

// Permission check props
export interface PermissionGuardProps {
  children: React.ReactNode;
  permission: Permission;
  fallback?: React.ReactNode;
}

// Authentication service interface
export interface IAuthService {
  // Core methods
  initializeApp(): Promise<BootstrapResult>;
  getAuthContext(): AuthContext | null;
  clearCache(): void;
  
  // Role checks
  isAdmin(): boolean;
  isTenant(): boolean;
  isUnassigned(): boolean;
  hasRole(role: UserRole): boolean;
  
  // Permission checks
  hasPermission(permission: Permission): boolean;
  hasPermissions(permissions: Permission[]): boolean;
  
  // Getters
  getUserId(): string | null;
  getTenantId(): string | null;
  getAdminId(): string | null;
  
  // State
  getState(): AuthState;
}

// RPC function types
export interface GetAuthContextResponse {
  role: UserRole;
  user_id: string | null;
  tenant_id: string | null;
  admin_id?: string;
  admin_email?: string;
  full_name?: string;
  admin_role?: string;
  permissions?: Record<string, any>;
  capabilities?: Permission[];
  tenant_name?: string;
  tenant_slug?: string;
  tenant_plan?: string;
  tenant_status?: string;
  user_role?: string;
  user_permissions?: Permission[];
  profile?: UserProfile;
  is_active: boolean;
  needs_onboarding?: boolean;
  last_login_at: string;
  error?: string;
}

// Error types
export class AuthError extends Error {
  constructor(
    message: string,
    public code: string,
    public context?: Partial<AuthContext>
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Common error codes
export const AUTH_ERROR_CODES = {
  NOT_AUTHENTICATED: 'NOT_AUTHENTICATED',
  NOT_ADMIN: 'NOT_ADMIN',
  NOT_TENANT: 'NOT_TENANT',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  ADMIN_CALLED_TENANT_FUNCTION: 'ADMIN_CALLED_TENANT_FUNCTION',
  UNKNOWN_ROLE: 'UNKNOWN_ROLE',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
} as const;

// Type guards
export function isAdminContext(context: AuthContext): context is AdminAuthContext {
  return context.role === 'admin';
}

export function isTenantContext(context: AuthContext): context is TenantAuthContext {
  return context.role === 'tenant';
}

export function isUnassignedContext(context: AuthContext): context is UnassignedAuthContext {
  return context.role === 'unassigned';
}

export function isErrorContext(context: AuthContext): context is ErrorAuthContext {
  return context.role === 'error';
}

// Utility types
export type RoleBasedComponent<T = {}> = T & {
  role: UserRole;
};

export type WithAuthContext<T = {}> = T & {
  authContext: AuthContext;
};

// Configuration types
export interface AuthConfig {
  autoRefresh: boolean;
  sessionTimeout: number;
  retryAttempts: number;
  retryDelay: number;
}

// Default configuration
export const DEFAULT_AUTH_CONFIG: AuthConfig = {
  autoRefresh: true,
  sessionTimeout: 30 * 60 * 1000, // 30 minutes
  retryAttempts: 3,
  retryDelay: 1000,
};
